import { getUiIntlLocale } from '@/i18n/languageContext';

/** French short weekdays: first 3 letters + period (Sunday-first). */
export const FR_WEEKDAY_SHORT_SUN_FIRST = [
  'dim.',
  'lun.',
  'mar.',
  'mer.',
  'jeu.',
  'ven.',
  'sam.',
] as const;

/** French short weekdays: first 3 letters + period (Monday-first). */
export const FR_WEEKDAY_SHORT_MON_FIRST = [
  'lun.',
  'mar.',
  'mer.',
  'jeu.',
  'ven.',
  'sam.',
  'dim.',
] as const;

export function isFrenchIntlLocale(locale: string): boolean {
  return locale.toLowerCase().startsWith('fr');
}

type FormatWeekdayShortOptions = {
  uppercase?: boolean;
};

/**
 * Localized short weekday for a calendar date.
 * French UI always uses exactly three letters plus a period (lun., mar., …).
 */
export function formatIntlWeekdayShort(
  date: Date,
  locale = getUiIntlLocale(),
  options?: FormatWeekdayShortOptions,
): string {
  if (isFrenchIntlLocale(locale)) {
    const label = FR_WEEKDAY_SHORT_SUN_FIRST[date.getDay()] ?? FR_WEEKDAY_SHORT_SUN_FIRST[0];
    return options?.uppercase ? label.toUpperCase() : label;
  }
  const formatted = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  return options?.uppercase ? formatted.toUpperCase() : formatted;
}

/** Sunday-first weekday labels from the active UI locale (e.g. Su/Mo or dim./lun.). */
export function intlWeekdayLabels(
  locale = getUiIntlLocale(),
  style: 'short' | 'narrow' = 'short',
): string[] {
  if (isFrenchIntlLocale(locale) && style === 'short') {
    return [...FR_WEEKDAY_SHORT_SUN_FIRST];
  }
  const sunday = new Date(2024, 0, 7);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(sunday);
    date.setDate(sunday.getDate() + index);
    return new Intl.DateTimeFormat(locale, { weekday: style }).format(date);
  });
}

/** Localized month + year title for calendar popovers. */
export function intlMonthYearLabel(date: Date, locale = getUiIntlLocale()): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
}

/** Monday-first short weekday labels (e.g. Mon/lun.). */
export function intlWeekdayLabelsMonFirst(locale = getUiIntlLocale()): string[] {
  if (isFrenchIntlLocale(locale)) {
    return [...FR_WEEKDAY_SHORT_MON_FIRST];
  }
  const monday = new Date(2024, 0, 1);
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  });
}
